import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import { SnakbarService } from './snakbar.service';
import {
  AccordionTabs,
  AttachmentDownloadRequestType,
  dateTimeFormat,
  downloadInfoPageSectionHeaders,
  downloadInfoPageSectionKeys,
  fileFormats,
  fileFormatsType,
  ModalPopupMessages,
} from '../utility/constants';
import { GridEventColumns } from '../components/events/event.config';
import { AttachmentsHeaders, NotesHeaders } from '../configs/common-configs';
import {
  RolesPermissionsExportAssignment,
  sanitizeExcelWorksheetName,
  writeRolesPermissionsAssignmentToWorksheet,
  writeRolesPermissionsModuleWiseToWorksheet,
  writeUserRolesPermissionsExportToWorksheet,
  writeUserRolesPermissionsOnlyToWorksheet,
  writeUserSummaryTableToWorksheet,
} from '../utility/roles-permissions-export.util';

@Injectable({
  providedIn: 'root',
})
export class DownloadFileService {
  constructor(
    private readonly datePipe: DatePipe,
    private readonly snakbarService: SnakbarService
  ) {}

public generateExcelCSVFile(excelData: any,specialKeyData?:any): void {  
    const workbook = new ExcelJS.Workbook(); // create workbook
    if (specialKeyData?.isUserRolesPermissionsExport) {
      this.loadUserRolesPermissionsExportWorkbook(workbook, excelData, specialKeyData);
    } else if (specialKeyData?.isRolesPermissionsModuleWiseExport) {
      this.loadRolesPermissionsModuleWiseWorkbook(workbook, excelData);
    } else {
      const worksheetName =
        specialKeyData?.appendRolesPermissions && excelData.extOrNumber
          ? sanitizeExcelWorksheetName(excelData.extOrNumber)
          : excelData.name;
      const worksheet = workbook.addWorksheet(worksheetName);
      this.loadHeadersAndDataBasedOnPage(excelData, worksheet, specialKeyData);
      this.autoWidth(worksheet);
      worksheet.eachRow(function (Row) {
        Row.alignment = { horizontal: 'left' };
      });
    }
    this.saveFile(
      excelData.selectedFileType === fileFormats.csv
        ? workbook.csv
        : workbook.xlsx,
      excelData
    ); // save file & download
    this.snakbarService.openCustomisedSnackBar(
      ModalPopupMessages.downloadSuccess(excelData.successMessage),
      ModalPopupMessages.success
    );
  }

  private loadRolesPermissionsModuleWiseWorkbook(
    workbook: ExcelJS.Workbook,
    excelData: any
  ): void {
    const assignment: RolesPermissionsExportAssignment | undefined =
      excelData.rolesPermissionsAssignments?.[0];
    const worksheetName = sanitizeExcelWorksheetName(
      excelData.extOrNumber || excelData.name
    );
    const worksheet = workbook.addWorksheet(worksheetName);
    if (assignment) {
      writeRolesPermissionsModuleWiseToWorksheet(worksheet, assignment);
    }
    worksheet.eachRow(row => {
      row.alignment = { horizontal: 'left' };
    });
  }

  private loadUserRolesPermissionsExportWorkbook(
    workbook: ExcelJS.Workbook,
    excelData: any,
    specialKeyData: any
  ): void {
    const users = specialKeyData.usersExportBlocks ?? [];
    const usedSheetNames = new Set<string>();
    const isCsv = excelData.selectedFileType === fileFormats.csv;

    if (isCsv || users.length <= 1) {
      const worksheetName = isCsv
        ? excelData.name
        : sanitizeExcelWorksheetName(
            users[0]?.fullName ?? excelData.name,
            usedSheetNames
          );
      const worksheet = workbook.addWorksheet(worksheetName);
      writeUserRolesPermissionsExportToWorksheet(worksheet, users);
      worksheet.eachRow(row => {
        row.alignment = { horizontal: 'left' };
      });
      return;
    }

    const summarySheetName = sanitizeExcelWorksheetName(
      excelData.name,
      usedSheetNames
    );
    const summarySheet = workbook.addWorksheet(summarySheetName);
    writeUserSummaryTableToWorksheet(summarySheet, users);
    summarySheet.eachRow(row => {
      row.alignment = { horizontal: 'left' };
    });

    for (const user of users) {
      if (!(user.rolesPermissions?.length)) {
        continue;
      }
      const worksheetName = sanitizeExcelWorksheetName(
        user.fullName ?? excelData.name,
        usedSheetNames
      );
      const worksheet = workbook.addWorksheet(worksheetName);
      writeUserRolesPermissionsOnlyToWorksheet(worksheet, user);
      worksheet.eachRow(row => {
        row.alignment = { horizontal: 'left' };
      });
    }
  }

  private loadHeadersAndDataBasedOnPage(
    excelData: any,
    worksheet: ExcelJS.Worksheet,
    specialKeyData?:any
  ) {
    if (excelData.isFromSummary) {
    const summary = specialKeyData?.summaryTable;
    if (summary && Array.isArray(summary.headers) && Array.isArray(summary.values)) {
      // add header row for summary table
      const summaryHeaderRow = worksheet.addRow(summary.headers);
      summaryHeaderRow.font = { bold: false };
      this.getBorders(summaryHeaderRow, worksheet);
      const summaryValueRow = worksheet.addRow(summary.values);
      this.getBorders(summaryValueRow, worksheet);
    }

      worksheet.addRow(excelData.headers);
      if (specialKeyData?.isRangeInfo) {
        this.setDataForHeaderForNumberRanges(
          excelData,
          worksheet,
          'tableData',
          excelData.isFromSummary
        );
        
      }  else if(specialKeyData?.isApChargeCode){
        this.setDataForHeaderForApChargeCode(
              excelData,
              worksheet,
              'tableData',
              excelData.isFromSummary
          );
      } else if(specialKeyData?.isCurrencyInfo){
        this.setDataForForCurrecnyInfo(
              excelData,
              worksheet,
              'tableData',
              excelData.isFromSummary
          );
      } else {
        this.setDataForHeaders(excelData, worksheet, 'tableData'); // adding row data's
      }
    } else {
      excelData.sectionNames.forEach((headerName: string) => {
        const sectionData =
          excelData.pageData[this.getHeaderSectionKeys(headerName)];
        // Skip sections with no data
        if (!sectionData?.headers || sectionData?.data?.length === 0) {
          return;
        }
        const sectionHeaderRow = worksheet.addRow([headerName]); // adding section headers
        sectionHeaderRow.font = { bold: true };
        const sectionColumnHeader = worksheet.addRow(
          this.getHeaders(excelData, headerName)
        ); // adding headers
        sectionColumnHeader.font = { bold: true };
        this.getBorders(sectionColumnHeader, worksheet);
        // Use standard data headers method - data is already properly formatted in component
        this.setDataForHeaders(sectionData, worksheet, 'data'); // adding row data's
        worksheet.addRow([]); // adding empty row
      });

      const rolesPermissionsAssignments: RolesPermissionsExportAssignment[] =
        excelData.rolesPermissionsAssignments ?? [];
      if (specialKeyData?.appendRolesPermissions && rolesPermissionsAssignments.length) {
        for (const assignment of rolesPermissionsAssignments) {
          writeRolesPermissionsAssignmentToWorksheet(worksheet, assignment, {
            includeAssignmentHeading:
              specialKeyData?.includeRolesPermissionsAssignmentHeading !== false,
          });
        }
      }
    }
  }

  private getHeaders(excelData: any, headerName: string) {
    const sectionKey = this.getHeaderSectionKeys(headerName);
    const section = excelData.pageData[sectionKey];
    if (!section?.headers) {
      return [];
    }

    if (
      headerName === downloadInfoPageSectionHeaders.mawbInfo ||
      headerName === downloadInfoPageSectionHeaders.mawbmiscellaneous ||
      headerName === downloadInfoPageSectionHeaders.details ||
      headerName === downloadInfoPageSectionHeaders.mawbManifests ||
      headerName === downloadInfoPageSectionHeaders.mawbFlights ||
      headerName === downloadInfoPageSectionHeaders.manifestInfo ||
      headerName === downloadInfoPageSectionHeaders.manifestBag ||
      headerName === downloadInfoPageSectionHeaders.widgets ||
      headerName === downloadInfoPageSectionHeaders.hawbInfo ||
      headerName === downloadInfoPageSectionHeaders.hawbCommodity ||
      headerName === downloadInfoPageSectionHeaders.hawbRoles ||
      headerName === downloadInfoPageSectionHeaders.hawbMiscellaneous ||
      headerName === downloadInfoPageSectionHeaders.hawbStatusCode ||
      headerName === downloadInfoPageSectionHeaders.hawbConsignee ||
      headerName === downloadInfoPageSectionHeaders.hawbShipper ||
      headerName === downloadInfoPageSectionHeaders.bagInfo ||
      headerName === downloadInfoPageSectionHeaders.bagMiscellaneous ||
      headerName === downloadInfoPageSectionHeaders.bagPieces ||
      headerName === downloadInfoPageSectionHeaders.bagCriteria ||
      headerName === downloadInfoPageSectionHeaders.userInfo ||
      headerName === downloadInfoPageSectionHeaders.userDepartments ||
      headerName === downloadInfoPageSectionHeaders.loginInfo ||
      headerName === downloadInfoPageSectionHeaders.agentInfo ||
      headerName === downloadInfoPageSectionHeaders.agentAddress ||
      headerName === downloadInfoPageSectionHeaders.address ||
      headerName === downloadInfoPageSectionHeaders.hsCodeInfo ||
      headerName === downloadInfoPageSectionHeaders.numberRange ||
      headerName === downloadInfoPageSectionHeaders.numberRangeDetails ||
      headerName === downloadInfoPageSectionHeaders.numberRanges ||
      headerName === downloadInfoPageSectionHeaders.airlineIn ||
      headerName === downloadInfoPageSectionHeaders.airlineAddress ||
      headerName === downloadInfoPageSectionHeaders.airportIn ||
      headerName === downloadInfoPageSectionHeaders.airportAddress ||
      headerName === downloadInfoPageSectionHeaders.accountInfo ||
      headerName === downloadInfoPageSectionHeaders.accountAddress ||

      headerName === downloadInfoPageSectionHeaders.billingAddress ||
      headerName === downloadInfoPageSectionHeaders.governmentMasterShipmentBilling ||
      headerName === downloadInfoPageSectionHeaders.governmentInformation ||
      headerName === downloadInfoPageSectionHeaders.customerService ||
      headerName === downloadInfoPageSectionHeaders.businessInformation ||
      headerName === downloadInfoPageSectionHeaders.customerManagement ||
      headerName === downloadInfoPageSectionHeaders.users ||
      headerName === downloadInfoPageSectionHeaders.details ||
       headerName === downloadInfoPageSectionHeaders.apChargeCodeInfo ||
      headerName === downloadInfoPageSectionHeaders.apChargeCode  ||
      headerName === downloadInfoPageSectionHeaders.deliveryZoneInfo ||
      headerName === downloadInfoPageSectionHeaders.deliveryZoneInfoTable ||
      headerName === downloadInfoPageSectionHeaders.rateScaleInfo ||
      headerName === downloadInfoPageSectionHeaders.rateScaleAccessorialCharges ||
      headerName === downloadInfoPageSectionHeaders.rateScaleWeightBasedCharges ||
      headerName === downloadInfoPageSectionHeaders.apInvoiceInfo ||
      headerName === downloadInfoPageSectionHeaders.apCharges ||
      headerName === downloadInfoPageSectionHeaders.arInvoiceInfo ||
      headerName === downloadInfoPageSectionHeaders.arCharges ||
      headerName === downloadInfoPageSectionHeaders.routingInfo ||
      headerName === downloadInfoPageSectionHeaders.routes ||
      headerName === downloadInfoPageSectionHeaders.transitPoints
    ) {
      return section.headers;
    } else if (headerName === downloadInfoPageSectionHeaders.events) {
      return GridEventColumns.map(eventItem => eventItem.label);
    } else if (headerName === downloadInfoPageSectionHeaders.notes) {
      return NotesHeaders.map(eventItem => eventItem.label);
    } else if (headerName === downloadInfoPageSectionHeaders.attachments) {
      return AttachmentsHeaders.map(eventItem => eventItem.label);
    }
  }

  private getHeaderSectionKeys(sectionName: string) {
    const sectionKeyMap = {
      [AttachmentDownloadRequestType.mawbInfo]:
        downloadInfoPageSectionKeys.mawbInfo,
      [downloadInfoPageSectionHeaders.mawbmiscellaneous]:
        downloadInfoPageSectionKeys.mawbmiscellaneous,
      [AccordionTabs.Details]: downloadInfoPageSectionKeys.details,
      [AccordionTabs.Events]: downloadInfoPageSectionKeys.events,
      [AccordionTabs.Attachments]: downloadInfoPageSectionKeys.attachments,
      [AccordionTabs.Notes]: downloadInfoPageSectionKeys.notes,
      [downloadInfoPageSectionHeaders.mawbManifests]:
        downloadInfoPageSectionKeys.mawbManifests,
      [downloadInfoPageSectionHeaders.mawbFlights]:
        downloadInfoPageSectionKeys.mawbFlights,
      [downloadInfoPageSectionHeaders.manifestInfo]:
        downloadInfoPageSectionKeys.manifestInfo,
      [downloadInfoPageSectionHeaders.manifestBag]:
        downloadInfoPageSectionKeys.manifestsBag,
      [downloadInfoPageSectionHeaders.widgets]:
        downloadInfoPageSectionKeys.widgets,
      [downloadInfoPageSectionHeaders.hawbInfo]:
        downloadInfoPageSectionKeys.hawbInfo,
      [downloadInfoPageSectionHeaders.hawbCommodity]:
        downloadInfoPageSectionKeys.hawbCommodity,
      [downloadInfoPageSectionHeaders.hawbRoles]:
        downloadInfoPageSectionKeys.hawbRoles,
      [downloadInfoPageSectionHeaders.hawbMiscellaneous]:
        downloadInfoPageSectionKeys.hawbMiscellaneous,
      [downloadInfoPageSectionHeaders.hawbStatusCode]:
        downloadInfoPageSectionKeys.hawbStatusCode,
      [downloadInfoPageSectionHeaders.hawbConsignee]:
        downloadInfoPageSectionKeys.hawbConsignee,
      [downloadInfoPageSectionHeaders.hawbShipper]:
        downloadInfoPageSectionKeys.hawbShipper,
      [downloadInfoPageSectionHeaders.bagInfo]:
        downloadInfoPageSectionKeys.bagInfo,
      [downloadInfoPageSectionHeaders.bagMiscellaneous]:
        downloadInfoPageSectionKeys.bagMiscellaneous,
      [downloadInfoPageSectionHeaders.bagPieces]:
        downloadInfoPageSectionKeys.bagPieces,
      [downloadInfoPageSectionHeaders.bagCriteria]:
        downloadInfoPageSectionKeys.bagCriteria,
      [downloadInfoPageSectionHeaders.userInfo]:
        downloadInfoPageSectionKeys.userInfo,
      [downloadInfoPageSectionHeaders.userDepartments]:
        downloadInfoPageSectionKeys.userDepartments,
      [downloadInfoPageSectionHeaders.loginInfo]:
        downloadInfoPageSectionKeys.loginInfo,
      [downloadInfoPageSectionHeaders.agentInfo]:
        downloadInfoPageSectionKeys.agentInfo,
      [downloadInfoPageSectionHeaders.agentAddress]:
        downloadInfoPageSectionKeys.agentAddress,
      [downloadInfoPageSectionHeaders.address]:
        downloadInfoPageSectionKeys.agentAddress,
      [downloadInfoPageSectionHeaders.hsCodeInfo]:
        downloadInfoPageSectionKeys.hsCode,
      [downloadInfoPageSectionHeaders.numberRange]:
        downloadInfoPageSectionKeys.numberRange,
      [downloadInfoPageSectionHeaders.numberRangeDetails]:
        downloadInfoPageSectionKeys.numberRangeDetails,
      [downloadInfoPageSectionHeaders.numberRanges]:
        downloadInfoPageSectionKeys.numberRanges,
      [downloadInfoPageSectionHeaders.airlineIn]:
        downloadInfoPageSectionKeys.airlineInfo,
      [downloadInfoPageSectionHeaders.airlineAddress]:
        downloadInfoPageSectionKeys.airlineAddress,
      [downloadInfoPageSectionHeaders.airportIn]:
        downloadInfoPageSectionKeys.airportInfo,
      [downloadInfoPageSectionHeaders.airportAddress]:
        downloadInfoPageSectionKeys.airportAddress,
      [downloadInfoPageSectionHeaders.accountInfo]:
        downloadInfoPageSectionKeys.accountInfo,
      [downloadInfoPageSectionHeaders.accountAddress]:
        downloadInfoPageSectionKeys.accountAddress,
       [downloadInfoPageSectionHeaders.billingAddress]:
        downloadInfoPageSectionKeys.billingAddress,
      [downloadInfoPageSectionHeaders.businessInformation]:
        downloadInfoPageSectionKeys.businessInformation,
      [downloadInfoPageSectionHeaders.customerManagement]:
        downloadInfoPageSectionKeys.customerManagement,
       [downloadInfoPageSectionHeaders.users]:
        downloadInfoPageSectionKeys.users,
       [downloadInfoPageSectionHeaders.users]:
        downloadInfoPageSectionKeys.users,
     [downloadInfoPageSectionHeaders.apChargeCodeInfo]:
        downloadInfoPageSectionKeys.apChargeCodeInfo,
    [downloadInfoPageSectionHeaders.apChargeCode]:
        downloadInfoPageSectionKeys.apChargeCode,
     [downloadInfoPageSectionHeaders.deliveryZoneInfo]:
        downloadInfoPageSectionKeys.deliveryZoneInfo,
    [downloadInfoPageSectionHeaders.deliveryZoneInfoTable]:
        downloadInfoPageSectionKeys.deliveryZoneInfoTable,
    [downloadInfoPageSectionHeaders.rateScaleInfo]:
        downloadInfoPageSectionKeys.rateScaleInfo,
    [downloadInfoPageSectionHeaders.rateScaleAccessorialCharges]:
        downloadInfoPageSectionKeys.rateScaleAccessorialCharges,
    [downloadInfoPageSectionHeaders.rateScaleWeightBasedCharges]:
        downloadInfoPageSectionKeys.rateScaleWeightBasedCharges,
    [downloadInfoPageSectionHeaders.apInvoiceInfo]:
        downloadInfoPageSectionKeys.apInvoiceInfo,
    [downloadInfoPageSectionHeaders.apCharges]:
        downloadInfoPageSectionKeys.apCharges,
    [downloadInfoPageSectionHeaders.governmentInformation]:
        downloadInfoPageSectionKeys.governmentInformation,
    [downloadInfoPageSectionHeaders.governmentMasterShipmentBilling]:
        downloadInfoPageSectionKeys.governmentMasterShipmentBilling,
    [downloadInfoPageSectionHeaders.details]:
        downloadInfoPageSectionKeys.details,
    [downloadInfoPageSectionHeaders.customerService]:
        downloadInfoPageSectionKeys.customerService,
      [downloadInfoPageSectionHeaders.arInvoiceInfo]:
        downloadInfoPageSectionKeys.arInvoiceInfo,
      [downloadInfoPageSectionHeaders.arCharges]:
        downloadInfoPageSectionKeys.arCharges,
      [downloadInfoPageSectionHeaders.routingInfo]:
        downloadInfoPageSectionKeys.routingInfo,
      [downloadInfoPageSectionHeaders.routes]: downloadInfoPageSectionKeys.routes,
      [downloadInfoPageSectionHeaders.transitPoints]:
        downloadInfoPageSectionKeys.transitPoints,
    };
    return sectionKeyMap[sectionName] || '';
  }

  private getBorders(dataToIterate: ExcelJS.Row, worksheet: ExcelJS.Worksheet) {
    dataToIterate.eachCell({ includeEmpty: true }, (cell_v, colNumber) => {
      if (colNumber <= worksheet.columnCount) {
        cell_v.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      }
    });
  }

  private setDataForHeaders(
    excelData: any,
    worksheet: ExcelJS.Worksheet,
    param: any
  ) {
    if (!excelData[param] || !Array.isArray(excelData[param])) {
      return; // Skip if data is undefined or not an array
    }
    excelData[param].forEach((item: any) => {
      const row: any[] = [];
      excelData.headerKeys.forEach((header: string) => {
        row.push(item[header]);
      });
      const rowData = worksheet.addRow(row);
      this.getBorders(rowData, worksheet);
    });
  }

  private readonly autoWidth = (worksheet: any, minimalWidth = 10) => {
     if (!worksheet.columns) return;
    worksheet.columns.forEach((column: any) => {
      let maxColumnLength = 0;
      column.eachCell({ includeEmpty: true }, (cell: any) => {
        maxColumnLength = Math.max(
          maxColumnLength,
          minimalWidth,
          cell.value ? cell.value.toString().length : 0
        );
      });
      column.width = maxColumnLength + 2;
    });
  };

  private saveFile(workbookType: ExcelJS.Csv | ExcelJS.Xlsx, excelData: any) {
    workbookType.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], {
        type:
          excelData.selectedFileType === fileFormats.csv
            ? fileFormatsType.csv
            : fileFormatsType.xlsx,
      });
      FileSaver(blob, this.getFileName(excelData));
    });
  }

  private getFileName(excelData: any) {
    const extension =
      excelData.selectedFileType === fileFormats.csv
        ? fileFormats.csv
        : fileFormats.xlsx;
    const dateTime = this.datePipe.transform(
      new Date(),
      dateTimeFormat.dateTime24hours
    );
    // Use extOrNumber if available, otherwise fall back to name
    const baseName = excelData.extOrNumber || excelData.name;
    return `${baseName}_${dateTime}.${extension}`;
  }

  private setDataForHeaderForNumberRanges(
    excelData: any,
    worksheet: ExcelJS.Worksheet,
    param: any,
    isFromSummary: boolean
  ) {
    let topHeaders = isFromSummary
      ? ['', '', '', '', '', 'Start No.', 'End No.', 'Last Used', 'Priority']
      : [
          '',
          '',
          '',
          '',
          '',
          '',
          'Start No.',
          'End No.',
          'Last Used',
          'Priority',
        ];
    // Add top headers
    const headerRow = worksheet.addRow(topHeaders);
    this.getBorders(headerRow, worksheet);
    // Write each parent row with its setRange as sub-rows
    excelData[param].forEach((item: any) => {
      const setRanges = item.setRange ?? [];
      if (setRanges.length === 0) {
        let row: any;
        if (isFromSummary) {
          row = worksheet.addRow([
            item.numberRangeTypeName ?? '',
            item.seriesName ?? '',
            item.prefix ?? '',
            item.facility ?? '',
            item.additionalInformation ?? '',
            '',
            '',
            '',
            '',
          ]);
        } else {
          row = worksheet.addRow([
            item.numberRangeTypeName ?? '',
            item.number ?? '',
            item.seriesName ?? '',
            item.prefix ?? '',
            item.facility ?? '',
            item.additionalInformation ?? '',
            '',
            '',
            '',
            '',
          ]);
        }
        this.getBorders(row, worksheet);
        return;
      }

      if (isFromSummary) {
        setRanges.forEach((range: any, index: number) => {
          const rowDataForSummary = worksheet.addRow([
            index === 0 ? item.numberRangeTypeName : '',
            index === 0 ? item.seriesName : '',
            index === 0 ? item.prefix : '',
            index === 0 ? item.facility : '',
            index === 0 ? item.additionalInformation : '',
            range.startNumber,
            range.endNumber,
            range.lastUsedNumber,
            range.priority,
          ]);
          this.getBorders(rowDataForSummary, worksheet);
        });
      } else {
        setRanges.forEach((range: any, index: number) => {
          const rowDataforInfo = worksheet.addRow([
            index === 0 ? item.numberRangeTypeName : '',
            index === 0 ? item.number : '',
            index === 0 ? item.seriesName : '',
            index === 0 ? item.prefix : '',
            index === 0 ? item.facility : '',
            index === 0 ? item.additionalInformation : '',
            range.startNumber,
            range.endNumber,
            range.lastUsedNumber,
            range.priority,
          ]);
          this.getBorders(rowDataforInfo, worksheet);
        });
      }
    });
  }
 setDataForHeaderForApChargeCode(excelData: any,
    worksheet: ExcelJS.Worksheet,
    param: any,
    isFromSummary: boolean
  ) {
     let topHeaders = isFromSummary
      ? ['', '', '', 'Agent Charge Type', 'IBC Charge Code', 'Charge Type', 'Agent Charge Description']
      : [
          '',
          '',
          '',
          'Agent Charge Type',
          'IBC Charge Code',
          'Charge Type',
          'Agent Charge Description',
        ];
    // Add top headers
    const headerRow = worksheet.addRow(topHeaders);
    this.getBorders(headerRow, worksheet);
    // Write each parent row with its setRange as sub-rows
    excelData[param].forEach((item: any) => {
      const apChargeCodeDetails = item.apChargeCodeDetails ?? [];
      console.log('jnjnj',apChargeCodeDetails)
      console.log(item)
      if (apChargeCodeDetails.length === 0) {
        let row: any;
        if (isFromSummary) {
          row = worksheet.addRow([
            item.agentCode ?? '',
            item.agentName ?? '',
            item.description ?? '',
            '',
            '',
            '',
          ]);
        } 
        this.getBorders(row, worksheet);
        return;
      }

      if (isFromSummary) {
        apChargeCodeDetails.forEach((code: any, index: number) => {
          const rowDataForSummary = worksheet.addRow([
            index === 0 ? item.agentCode : '',
            index === 0 ? item.agentName : '',
            index === 0 ? item.description : '',
            code.agentChargeCode,
            code.ibcChargeCode,
            code.chargeType,
            code.description,
          ]);
          this.getBorders(rowDataForSummary, worksheet);
        });
      } 
    });
  }

setDataForForCurrecnyInfo(excelData: any,
    worksheet: ExcelJS.Worksheet,
    param: any,
    isFromCMSummary: boolean
  ) {
     let topCMHeaders = isFromCMSummary
      ? ['', '', '', 'Previous Conversion Rate', 'Updated Conversion Rate', 'Updated Date']
      : [
          '',
          '',
          '',
          'Previous Conversion Rate',
          'Updated Conversion Rate',
          'Updated Date',
        ];
    const detailsHeaderRow = worksheet.addRow(topCMHeaders);
    this.getBorders(detailsHeaderRow, worksheet);
    excelData[param].forEach((item: any) => {
      const currencyDetails = item.details ?? [];
      if (currencyDetails.length === 0) {
        let row: any;
        if (isFromCMSummary) {
          row = worksheet.addRow([
            item.previousConversionRate ?? '',
            item.updatedConversionRate ?? '',
            item.updatedDate ?? '',
            '',
            '',
            '',
          ]);
        } 
        this.getBorders(row, worksheet);
        return;
      }
      if (isFromCMSummary) {
        currencyDetails.forEach((code: any, index: number) => {
          const rowDataForSummary = worksheet.addRow([
            index === 0 ? item.currencyCode : '',
            index === 0 ? item.currencyName : '',
            index === 0 ? item.conversionRate : '',
            code.previousConversionRate,
            code.updatedConversionRate,
            code.updatedDate
          ]);
          this.getBorders(rowDataForSummary, worksheet);
        });
      } 
    });
  }
}

